/*
 * wa-tel-autolink.js
 * ------------------
 * Turns phone numbers that appear as plain text into tel: links.
 *
 * Why this exists rather than hard-coded <a href="tel:"> in each template:
 * the numbers live in merchant-editable content (section settings and rich
 * text on the homepage, product templates and the Help Centre). Baking links
 * into that content means they are lost the next time someone edits the text
 * in the theme editor. This runs once per page and covers existing content
 * plus anything added later.
 *
 * Progressive enhancement: with JS off the numbers still read normally, they
 * just are not tappable.
 *
 * Opt out for a specific element by adding data-no-tel to it or any ancestor.
 */
(function () {
  'use strict';

  // North American numbers only. Two deliberate restrictions keep wire specs
  // and part numbers out: a bare run of digits never matches, and the
  // unbracketed form must use the SAME separator twice (the \1 backreference).
  // Without that, catalogue text like "500 250-1000" reads as a phone number.
  //   (833) 268-8644 / 1 (833) 268-8644 / 833-268-8644 / 833.268.8644 / +1 833 268 8644
  var PHONE = /(?:\+?1[\s.\-]?)?(?:\([2-9]\d{2}\)[\s.\-]?[2-9]\d{2}[\s.\-]\d{4}|[2-9]\d{2}([\s.\-])[2-9]\d{2}\1\d{4})/g;

  // Never touch text inside these: already-linked, interactive, or code.
  var SKIP_TAGS = {
    A: 1, SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1,
    SELECT: 1, OPTION: 1, BUTTON: 1, CODE: 1, PRE: 1, SVG: 1, TEMPLATE: 1
  };

  function isSkipped(node) {
    for (var el = node.parentNode; el && el.nodeType === 1; el = el.parentNode) {
      if (SKIP_TAGS[el.tagName]) { return true; }
      if (el.hasAttribute && el.hasAttribute('data-no-tel')) { return true; }
    }
    return false;
  }

  // "1 (833) 268-8644" -> "+18332688644". Ten digits get the +1 they are missing.
  function toHref(display) {
    var digits = display.replace(/[^0-9]/g, '');
    if (digits.length === 10) { return '+1' + digits; }
    if (digits.length === 11 && digits.charAt(0) === '1') { return '+' + digits; }
    return 'tel-invalid';
  }

  function linkify(textNode) {
    var text = textNode.nodeValue;
    PHONE.lastIndex = 0;

    var frag = null;
    var last = 0;
    var m;

    while ((m = PHONE.exec(text)) !== null) {
      var start = m.index;
      var end = start + m[0].length;

      // Reject if the match is really a slice out of a longer dashed code,
      // e.g. the "250-500-1000" inside "XHHW2-BLK-250-500-1000". A trailing
      // full stop is fine, so only digits and hyphens disqualify.
      var before = start > 0 ? text.charAt(start - 1) : '';
      var after = end < text.length ? text.charAt(end) : '';
      if (/[\d-]/.test(before) || /[\d-]/.test(after)) { continue; }

      var href = toHref(m[0]);
      if (href === 'tel-invalid') { continue; }

      if (!frag) { frag = document.createDocumentFragment(); }
      if (start > last) {
        frag.appendChild(document.createTextNode(text.slice(last, start)));
      }

      var a = document.createElement('a');
      a.href = 'tel:' + href;
      a.className = 'wa-tel-link';
      a.textContent = m[0];
      frag.appendChild(a);

      last = end;
    }

    if (!frag) { return false; }
    if (last < text.length) {
      frag.appendChild(document.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
    return true;
  }

  function run(root) {
    if (!root) { return 0; }
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.length < 7) {
          return NodeFilter.FILTER_REJECT;
        }
        // Cheap pre-check before the expensive walk up the tree.
        PHONE.lastIndex = 0;
        if (!PHONE.test(node.nodeValue)) { return NodeFilter.FILTER_REJECT; }
        if (isSkipped(node)) { return NodeFilter.FILTER_REJECT; }
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    // Collect first: replacing nodes while walking invalidates the walker.
    var targets = [];
    var n;
    while ((n = walker.nextNode())) { targets.push(n); }

    var count = 0;
    for (var i = 0; i < targets.length; i++) {
      if (targets[i].parentNode && linkify(targets[i])) { count++; }
    }
    return count;
  }

  // The generated links set no colour of their own, so they fall through to
  // the theme's global `a` rule and paint themselves dark navy. That is
  // invisible where a number sits on a dark panel, such as the homepage
  // call-to-action band. Inheriting from the surrounding text is correct on
  // both light and dark backgrounds, and .wa-tel-link outranks the bare `a`
  // selector so no !important is needed.
  function ensureStyle() {
    if (document.getElementById('wa-tel-link-style')) { return; }
    var s = document.createElement('style');
    s.id = 'wa-tel-link-style';
    s.textContent = '.wa-tel-link{color:inherit;text-decoration-color:currentColor}';
    (document.head || document.documentElement).appendChild(s);
  }

  function init() { ensureStyle(); run(document.body); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections re-rendered by the theme editor or by AJAX need a second pass.
  document.addEventListener('shopify:section:load', function (e) {
    run(e.target);
  });
})();
